//--------------------------------------------------------------------//
//******************** AgentSchedulingService ***************************//
//--------------------------------------------------------------------//
// API communication layer for an agent's scheduled tasks.
// Translates between the UI's field names (id, name, prompt, cron, enabled,
// plus the UI-only daysOfWeek/startTime/frequency) and the backend's wire
// field names (task_id, task_name, task_cron, task_prompt, active_task).
import { request } from '../agents.request.js';

// Builds the create/update request body, mapping only the keys present.
// `id` -> `task_id` so the client's self-generated id (Date.now()) becomes
// the backend's task_id, keeping later update/delete/toggle calls addressable.
function toBackendPayload(data) {
    const payload = {};
    if (data.id !== undefined) payload.task_id = data.id;
    if (data.name !== undefined) payload.task_name = data.name;
    if (data.prompt !== undefined) payload.task_prompt = data.prompt;
    if (data.cron !== undefined) payload.task_cron = data.cron;
    if (data.enabled !== undefined) payload.active_task = data.enabled;
    return payload;
}

// Reverses buildCronExpression() — maps a stored cron string back to the three
// UI-only fields so the edit dialog can be pre-filled from the cron alone.
function parseCronExpression(cron) {
    if (!cron) return { daysOfWeek: [], startTime: '09:00', frequency: 'daily' };

    const parts = cron.trim().split(/\s+/);
    if (parts.length !== 5) return { daysOfWeek: [], startTime: '09:00', frequency: 'daily' };

    const [minuteField, hourField, , , dowField] = parts;

    const daysOfWeek = dowField === '*'
        ? [0, 1, 2, 3, 4, 5, 6]
        : dowField.split(',').map(Number).filter(n => !isNaN(n));

    let frequency = 'daily';
    let startTime = '09:00';

    if (minuteField === '*') {
        frequency = '1min';
        startTime = '00:00';
    } else if (minuteField === '*/10') {
        frequency = '10min';
        startTime = '00:00';
    } else if (minuteField === '*/30') {
        frequency = '30min';
        startTime = '00:00';
    } else {
        const minuteParts = minuteField.split(',').map(Number);
        const allValid = minuteParts.every(n => !isNaN(n));
        if (allValid && minuteParts.length === 6 && minuteParts[1] - minuteParts[0] === 10) {
            frequency = '10min';
            startTime = `00:${String(minuteParts[0]).padStart(2, '0')}`;
        } else if (allValid && minuteParts.length === 2 && minuteParts[1] - minuteParts[0] === 30) {
            frequency = '30min';
            startTime = `00:${String(minuteParts[0]).padStart(2, '0')}`;
        } else if (hourField === '*') {
            frequency = 'hourly';
            const mm = String(Number(minuteField)).padStart(2, '0');
            startTime = `00:${mm}`;
        } else {
            frequency = 'daily';
            const hh = String(Number(hourField)).padStart(2, '0');
            const mm = String(Number(minuteField)).padStart(2, '0');
            startTime = `${hh}:${mm}`;
        }
    }

    return { daysOfWeek, startTime, frequency };
}

// Maps a raw backend task back to the UI shape used by agent-scheduling.tab.js.
// daysOfWeek/startTime/frequency are parsed from the stored cron expression so
// the edit dialog is pre-filled correctly when re-opening an existing schedule.
function toClientSchedule(task) {
    const parsed = parseCronExpression(task.task_cron);
    return {
        id: task.task_id,
        name: task.task_name,
        prompt: task.task_prompt,
        cron: task.task_cron,
        enabled: task.active_task,
        daysOfWeek: parsed.daysOfWeek,
        startTime: parsed.startTime,
        frequency: parsed.frequency
    };
}

angular.module('agentsTableModule')

    .service('AgentSchedulingService', [function () {
        // Fetches all scheduled tasks for an agent.
        this.getSchedules = async function (agentId) {
            const result = await request(`/agents/${encodeURIComponent(agentId)}/schedules`, {}, 'GET');
            return { schedules: (result.schedules || []).map(toClientSchedule) };
        };

        // Creates a new scheduled task for an agent.
        this.createSchedule = function (agentId, data) {
            return request(`/agents/${encodeURIComponent(agentId)}/schedules`, toBackendPayload(data));
        };

        // Updates a scheduled task's prompt/cron settings.
        this.updateSchedule = function (agentId, scheduleId, data) {
            return request(`/agents/${encodeURIComponent(agentId)}/schedules/${encodeURIComponent(scheduleId)}/update`, toBackendPayload(data));
        };

        // Deletes a scheduled task.
        this.deleteSchedule = function (agentId, scheduleId) {
            return request(`/agents/${encodeURIComponent(agentId)}/schedules/${encodeURIComponent(scheduleId)}/delete`);
        };

        // Enables or disables a scheduled task.
        this.toggleSchedule = function (agentId, scheduleId, enabled) {
            return request(`/agents/${encodeURIComponent(agentId)}/schedules/${encodeURIComponent(scheduleId)}/toggle`, { active_task: enabled });
        };

        // Manually triggers a scheduled task immediately (for testing without waiting for cron).
        this.runSchedule = function (agentId, taskId) {
            return request(`/agents/${encodeURIComponent(agentId)}/schedules/${encodeURIComponent(taskId)}/run`);
        };
    }]);
